// packages/shared/src/constants/symbol.constants.ts
// Single source of truth for symbol domain value-sets (ADR-20, section 5).

import type { ChipVariant } from "../types/smart-table";

/** Revision conflict returned by the existing provider-identity preview/apply contract. */
export const SYMBOL_PROVIDER_IDENTITY_CHANGED_CODE = "IDENTITY_CHANGED";

export const SYMBOL_PROVIDER_IDENTITY_PURPOSE = ["quote", "history"] as const;
export type TSymbolProviderIdentityPurpose =
  (typeof SYMBOL_PROVIDER_IDENTITY_PURPOSE)[number];
export const SYMBOL_PROVIDER_IDENTITY_PURPOSE_OBJ = {
  QUOTE: SYMBOL_PROVIDER_IDENTITY_PURPOSE[0],
  HISTORY: SYMBOL_PROVIDER_IDENTITY_PURPOSE[1],
} as const satisfies Record<string, TSymbolProviderIdentityPurpose>;

export const SYMBOL_PRICE_REFRESH_STATUS = ["updated", "unavailable"] as const;
export type TSymbolPriceRefreshStatus =
  (typeof SYMBOL_PRICE_REFRESH_STATUS)[number];
export const SYMBOL_PRICE_REFRESH_STATUS_OBJ = {
  UPDATED: "updated",
  UNAVAILABLE: "unavailable",
} as const satisfies Record<string, TSymbolPriceRefreshStatus>;

export const SYMBOL_PROVIDER_MATCH_OUTCOME = [
  "matched",
  "unmatched",
  "ambiguous",
  "conflict",
] as const;
export type TSymbolProviderMatchOutcome =
  (typeof SYMBOL_PROVIDER_MATCH_OUTCOME)[number];
export const SYMBOL_PROVIDER_MATCH_OUTCOME_OBJ = {
  MATCHED: "matched",
  UNMATCHED: "unmatched",
  AMBIGUOUS: "ambiguous",
  CONFLICT: "conflict",
} as const satisfies Record<string, TSymbolProviderMatchOutcome>;

export const PROVIDER_IDENTITY_BOOTSTRAP_MODE = ["dry-run", "apply"] as const;
export type TProviderIdentityBootstrapMode =
  (typeof PROVIDER_IDENTITY_BOOTSTRAP_MODE)[number];
export const PROVIDER_IDENTITY_BOOTSTRAP_MODE_OBJ = {
  DRY_RUN: PROVIDER_IDENTITY_BOOTSTRAP_MODE[0],
  APPLY: PROVIDER_IDENTITY_BOOTSTRAP_MODE[1],
} as const satisfies Record<string, TProviderIdentityBootstrapMode>;
// How a MOEX SECID was matched to a local Symbol.
export const MOEX_SECID_MATCH_SOURCE = ["approved-mapping", "local-code", "isin"] as const;
export type TMoexSecidMatchSource = (typeof MOEX_SECID_MATCH_SOURCE)[number];
export const MOEX_SECID_MATCH_SOURCE_OBJ = {
  APPROVED_MAPPING: MOEX_SECID_MATCH_SOURCE[0],
  LOCAL_CODE: MOEX_SECID_MATCH_SOURCE[1],
  ISIN: MOEX_SECID_MATCH_SOURCE[2],
} as const satisfies Record<string, TMoexSecidMatchSource>;
export const SYMBOL_PROVIDER_MATCH_OUTCOME_LABELS: Record<
  TSymbolProviderMatchOutcome,
  Record<string, string>
> = {
  matched: { "en-US": "Matched", "ru-RU": "Сопоставлено" },
  unmatched: { "en-US": "Unmatched", "ru-RU": "Не сопоставлено" },
  ambiguous: { "en-US": "Ambiguous", "ru-RU": "Неоднозначно" },
  conflict: { "en-US": "Conflict", "ru-RU": "Конфликт" },
};
export const SYMBOL_PROVIDER_MATCH_OUTCOME_VARIANTS: Record<
  TSymbolProviderMatchOutcome,
  ChipVariant
> = {
  matched: "emerald",
  unmatched: "secondary",
  ambiguous: "destructive",
  conflict: "destructive",
};

export const SYMBOL_PROVIDER_CHANGE_EVENT_STATUS = [
  "pending",
  "applied",
  "ambiguous",
  "ignored",
] as const;
export type TSymbolProviderChangeEventStatus =
  (typeof SYMBOL_PROVIDER_CHANGE_EVENT_STATUS)[number];
export const SYMBOL_PROVIDER_CHANGE_EVENT_STATUS_OBJ = {
  PENDING: "pending",
  APPLIED: "applied",
  AMBIGUOUS: "ambiguous",
  IGNORED: "ignored",
} as const satisfies Record<string, TSymbolProviderChangeEventStatus>;
export const SYMBOL_PROVIDER_CHANGE_EVENT_STATUS_LABELS: Record<
  TSymbolProviderChangeEventStatus,
  Record<string, string>
> = {
  pending: { "en-US": "Pending", "ru-RU": "Ожидает" },
  applied: { "en-US": "Applied", "ru-RU": "Применено" },
  ambiguous: { "en-US": "Ambiguous", "ru-RU": "Неоднозначно" },
  ignored: { "en-US": "Ignored", "ru-RU": "Пропущено" },
};
export const SYMBOL_PROVIDER_CHANGE_EVENT_STATUS_VARIANTS: Record<
  TSymbolProviderChangeEventStatus,
  ChipVariant
> = {
  pending: "secondary",
  applied: "emerald",
  ambiguous: "destructive",
  ignored: "outline",
};

export const SYMBOL_PROVIDER_SYNC_STATUS = [
  "idle",
  "running",
  "succeeded",
  "failed",
  "truncated",
] as const;
export type TSymbolProviderSyncStatus =
  (typeof SYMBOL_PROVIDER_SYNC_STATUS)[number];
export const SYMBOL_PROVIDER_SYNC_STATUS_OBJ = {
  IDLE: "idle",
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  TRUNCATED: "truncated",
} as const satisfies Record<string, TSymbolProviderSyncStatus>;
export const SYMBOL_PROVIDER_SYNC_STATUS_LABELS: Record<
  TSymbolProviderSyncStatus,
  Record<string, string>
> = {
  idle: { "en-US": "Idle", "ru-RU": "Ожидание" },
  running: { "en-US": "Running", "ru-RU": "Выполняется" },
  succeeded: { "en-US": "Succeeded", "ru-RU": "Успешно" },
  failed: { "en-US": "Failed", "ru-RU": "Ошибка" },
  truncated: { "en-US": "Truncated", "ru-RU": "Обрезано" },
};
export const SYMBOL_PROVIDER_SYNC_STATUS_VARIANTS: Record<
  TSymbolProviderSyncStatus,
  ChipVariant
> = {
  idle: "secondary",
  running: "default",
  succeeded: "emerald",
  failed: "destructive",
  truncated: "destructive",
};

export const SYMBOL_AI_ANALYSIS_SECTION = [
  "summary",
  "reports",
  "history",
] as const;
export type TSymbolAiAnalysisSection =
  (typeof SYMBOL_AI_ANALYSIS_SECTION)[number];
export const SYMBOL_AI_ANALYSIS_SECTION_OBJ = {
  SUMMARY: SYMBOL_AI_ANALYSIS_SECTION[0],
  REPORTS: SYMBOL_AI_ANALYSIS_SECTION[1],
  HISTORY: SYMBOL_AI_ANALYSIS_SECTION[2],
} as const satisfies Record<string, TSymbolAiAnalysisSection>;

// ── autoStatus ──────────────────────────────────────────────────────────────
// System-computed status based on financial analysis.
export const AUTO_STATUS = [
  "new",
  "no_interest",
  "very_expensive",
  "expensive",
  "to_study_base",
] as const;
export type TAutoStatus = (typeof AUTO_STATUS)[number];
export const AUTO_STATUS_OBJ = {
  NEW: "new",
  NO_INTEREST: "no_interest",
  VERY_EXPENSIVE: "very_expensive",
  EXPENSIVE: "expensive",
  TO_STUDY_BASE: "to_study_base",
} as const satisfies Record<string, TAutoStatus>;
export const AUTO_STATUS_LABELS: Record<TAutoStatus, Record<string, string>> = {
  new: { "en-US": "New", "ru-RU": "Новый" },
  no_interest: { "en-US": "Not interested", "ru-RU": "Не интересно" },
  very_expensive: { "en-US": "Very expensive", "ru-RU": "Очень дорого" },
  expensive: { "en-US": "Expensive", "ru-RU": "Дороговато" },
  to_study_base: { "en-US": "To study (base)", "ru-RU": "Изучить (базово)" },
};
export const AUTO_STATUS_VARIANTS: Record<TAutoStatus, ChipVariant> = {
  new: "default",
  no_interest: "destructive",
  very_expensive: "destructive",
  expensive: "outline",
  to_study_base: "secondary",
};

// ── manualStatus ────────────────────────────────────────────────────────────
// User-set status for manual tracking/decision.
export const MANUAL_STATUS = [
  "no_interest",
  "very_expensive",
  "expensive",
  "to_study_base",
  "to_study_shortlist",
  "to_track",
  "to_add",
  "in_portfolio",
  "for_future",
  "other",
] as const;
export type TManualStatus = (typeof MANUAL_STATUS)[number];
export const MANUAL_STATUS_OBJ = {
  NO_INTEREST: "no_interest",
  VERY_EXPENSIVE: "very_expensive",
  EXPENSIVE: "expensive",
  TO_STUDY_BASE: "to_study_base",
  TO_STUDY_SHORTLIST: "to_study_shortlist",
  TO_TRACK: "to_track",
  TO_ADD: "to_add",
  IN_PORTFOLIO: "in_portfolio",
  FOR_FUTURE: "for_future",
  OTHER: "other",
} as const satisfies Record<string, TManualStatus>;
export const MANUAL_STATUS_LABELS: Record<
  TManualStatus,
  Record<string, string>
> = {
  no_interest: { "en-US": "Not interested", "ru-RU": "Не интересно" },
  very_expensive: { "en-US": "Very expensive", "ru-RU": "Очень дорого" },
  expensive: { "en-US": "Expensive", "ru-RU": "Дороговато" },
  to_study_base: { "en-US": "To study (base)", "ru-RU": "Изучить (базово)" },
  to_study_shortlist: {
    "en-US": "To study (shortlist)",
    "ru-RU": "Изучить (шорт-лист)",
  },
  to_track: { "en-US": "To track", "ru-RU": "Отслеживать (чуток дорого)" },
  to_add: { "en-US": "To add", "ru-RU": "Добавить" },
  in_portfolio: { "en-US": "In portfolio", "ru-RU": "В портфеле" },
  for_future: { "en-US": "For future", "ru-RU": "На будущее" },
  other: { "en-US": "Other", "ru-RU": "Иное (уточнить)" },
};
export const MANUAL_STATUS_VARIANTS: Record<TManualStatus, ChipVariant> = {
  no_interest: "destructive",
  very_expensive: "destructive",
  expensive: "outline",
  to_study_base: "secondary",
  to_study_shortlist: "secondary",
  to_track: "secondary",
  to_add: "default",
  in_portfolio: "default",
  for_future: "secondary",
  other: "secondary",
};

// ── symbolPriceReturnMode ───────────────────────────────────────────────────
// Two-step return of a manual price to FMP: read-only preview, then guarded apply.
export const SYMBOL_PRICE_RETURN_MODE = ["preview", "apply"] as const;
export type TSymbolPriceReturnMode = (typeof SYMBOL_PRICE_RETURN_MODE)[number];
export const SYMBOL_PRICE_RETURN_MODE_OBJ = {
  PREVIEW: "preview",
  APPLY: "apply",
} as const satisfies Record<string, TSymbolPriceReturnMode>;

// Result statuses of the return-to-FMP flow besides the refresh statuses.
export const SYMBOL_PRICE_RETURN_STATUS = ["preview", "conflict"] as const;
export type TSymbolPriceReturnStatus = (typeof SYMBOL_PRICE_RETURN_STATUS)[number];
export const SYMBOL_PRICE_RETURN_STATUS_OBJ = {
  PREVIEW: "preview",
  CONFLICT: "conflict",
} as const satisfies Record<string, TSymbolPriceReturnStatus>;

// ── fmpIdentityEnrichment ───────────────────────────────────────────────────
export const FMP_IDENTITY_EVALUATION_STATUS = ["confirmed", "rejected"] as const;
export type TFmpIdentityEvaluationStatus = (typeof FMP_IDENTITY_EVALUATION_STATUS)[number];
export const FMP_IDENTITY_EVALUATION_STATUS_OBJ = {
  CONFIRMED: "confirmed",
  REJECTED: "rejected",
} as const satisfies Record<string, TFmpIdentityEvaluationStatus>;

export const FMP_IDENTITY_ENRICHMENT_STATUS = ["written", "conflict"] as const;
export type TFmpIdentityEnrichmentStatus = (typeof FMP_IDENTITY_ENRICHMENT_STATUS)[number];
export const FMP_IDENTITY_ENRICHMENT_STATUS_OBJ = {
  WRITTEN: "written",
  CONFLICT: "conflict",
} as const satisfies Record<string, TFmpIdentityEnrichmentStatus>;

// ── moexIsinSearchOutcome ───────────────────────────────────────────────────
// Result of looking a local ISIN up among MOEX securities.
export const MOEX_ISIN_SEARCH_OUTCOME = ["none", "unique", "ambiguous"] as const;
export type TMoexIsinSearchOutcome = (typeof MOEX_ISIN_SEARCH_OUTCOME)[number];
export const MOEX_ISIN_SEARCH_OUTCOME_OBJ = {
  NONE: "none",
  UNIQUE: "unique",
  AMBIGUOUS: "ambiguous",
} as const satisfies Record<string, TMoexIsinSearchOutcome>;

// ── moexMigrationDecisionStatus ─────────────────────────────────────────────
export const MOEX_MIGRATION_DECISION_STATUS = ["apply", "report"] as const;
export type TMoexMigrationDecisionStatus = (typeof MOEX_MIGRATION_DECISION_STATUS)[number];
export const MOEX_MIGRATION_DECISION_STATUS_OBJ = {
  APPLY: "apply",
  REPORT: "report",
} as const satisfies Record<string, TMoexMigrationDecisionStatus>;
